<script setup>
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import MainLayout from "@/components/layout/MainLayout.vue";
import ResidentProfileCard from "@/components/residents/ResidentProfileCard.vue";
import ResidentContactCard from "@/components/residents/ResidentContactCard.vue";
import ResidentFinancialSummaryCard from "@/components/residents/ResidentFinancialSummaryCard.vue";
import ResidentInvoiceHistory from "@/components/residents/ResidentInvoiceHistory.vue";
import ResidentPaymentHistory from "@/components/residents/ResidentPaymentHistory.vue";
import ResidentFormModal from "@/components/residents/ResidentFormModal.vue";
import { useResidentStore } from "@/stores/resident";
import { usePrint } from "@/composables/usePrint";

const route = useRoute();
const router = useRouter();
const residentStore = useResidentStore();
const activeTab = ref("invoices");

const residentFormModal = ref(null);

const residentId = computed(() => route.params.id);
const resident = computed(() =>
  residentStore.getResidentById(residentId.value),
);
const invoiceItems = computed(() =>
  residentStore.getInvoicesByResidentId(residentId.value),
);
const paymentItems = computed(() =>
  residentStore.getPaymentsByResidentId(residentId.value),
);

const goBack = () => router.push("/residents");
const residentTypeText = (type) =>
  ({ owner: "เจ้าของ", tenant: "ผู้เช่า" }[type] || type);

const handleEdit = () => residentFormModal.value?.open(resident.value);

const { printResident } = usePrint();
const handlePrint = () =>
  printResident(resident.value, invoiceItems.value, paymentItems.value);
</script>

<template>
  <MainLayout>
    <div class="container-fluid px-0">
      <div v-if="resident" class="vstack gap-4">
        <div
          class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3"
        >
          <div>
            <button
              type="button"
              class="btn btn-secondary border rounded-3 mb-3"
              @click="goBack"
            >
              <i class="bi bi-arrow-left me-1"></i>กลับไปรายการลูกบ้าน
            </button>
            <div class="d-flex align-items-center gap-3 flex-wrap">
              <div class="resident-icon">
                <i class="bi bi-person-circle"></i>
              </div>
              <div>
                <h3 class="fw-bold mb-1">{{ resident.name }}</h3>
                <div class="text-muted">
                  {{ resident.residentCode }} • {{ resident.unitCode }} •
                  {{ residentTypeText(resident.residentType) }}
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex gap-2 flex-wrap">
            <button
              type="button"
              class="btn btn-secondary rounded-3"
              @click="handlePrint"
            >
              <i class="bi bi-printer me-1"></i>พิมพ์ข้อมูลลูกบ้าน
            </button>
            <button
              type="button"
              class="btn btn-primary rounded-3"
              @click="handleEdit"
            >
              <i class="bi bi-pencil-square me-1"></i>แก้ไขลูกบ้าน
            </button>
          </div>
        </div>

        <div class="row g-4">
          <div class="col-xl-4">
            <ResidentContactCard :resident="resident" />
          </div>
          <div class="col-xl-4">
            <ResidentFinancialSummaryCard :resident="resident" />
          </div>
          <div class="col-xl-4">
            <ResidentProfileCard :resident="resident" />
          </div>
        </div>

        <div class="card border-0 shadow-sm rounded-4 overflow-hidden">
          <div class="card-header bg-white border-0 p-3">
            <ul class="nav nav-pills gap-2">
              <li class="nav-item">
                <button
                  type="button"
                  class="nav-link rounded-pill px-4"
                  :class="
                    activeTab === 'invoices' ? 'active' : 'text-dark bg-light'
                  "
                  @click="activeTab = 'invoices'"
                >
                  <i class="bi bi-receipt me-2"></i>ประวัติใบแจ้งหนี้
                </button>
              </li>
              <li class="nav-item">
                <button
                  type="button"
                  class="nav-link rounded-pill px-4"
                  :class="
                    activeTab === 'payments' ? 'active' : 'text-dark bg-light'
                  "
                  @click="activeTab = 'payments'"
                >
                  <i class="bi bi-cash-coin me-2"></i>ประวัติการชำระเงิน
                </button>
              </li>
            </ul>
          </div>
          <div class="card-body p-4 bg-light-subtle">
            <ResidentInvoiceHistory
              v-if="activeTab === 'invoices'"
              :items="invoiceItems"
            />
            <ResidentPaymentHistory v-else :items="paymentItems" />
          </div>
        </div>
      </div>

      <div v-else class="card border-0 shadow-sm rounded-4">
        <div class="card-body p-5 text-center">
          <i class="bi bi-search fs-1 text-secondary d-block mb-3"></i>
          <h4 class="fw-bold mb-2">ไม่พบข้อมูลลูกบ้าน</h4>
          <p class="text-muted mb-4">
            อาจเป็นเพราะลิงก์ไม่ถูกต้อง หรือยังไม่มีข้อมูลลูกบ้านนี้ในระบบ
          </p>
          <button
            type="button"
            class="btn btn-primary rounded-3 px-4"
            @click="goBack"
          >
            <i class="bi bi-arrow-left me-2"></i>กลับไปหน้ารายการลูกบ้าน
          </button>
        </div>
      </div>
    </div>

    <!-- ✅ Mount modal ไว้ที่นี่ -->
    <ResidentFormModal ref="residentFormModal" />
  </MainLayout>
</template>

<style scoped>
.resident-icon {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: rgba(13, 110, 253, 0.12);
  color: #0d6efd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}
</style>